import { AppSettings } from "@/AppSettings";
import { AuditType, ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, Participant, SourceData, SourceDataFieldType, SourceDataItem, SourceDataItemAudit, Trial } from "@/api/dtos";
import SourceDataDetails from "@/components/details/SourceDataDetails";
import { SourceDataItemMapping } from "@/components/forms/forms/source-data/values/Index";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialsContext from "@/contexts/data/TrialsContext";
import useItem, { createItem } from "@/hooks/form/source-data/useItem";
import auditTypeToText from "@/utilities/configuration/auditTypeToText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator, timelineDotClasses } from "@mui/lab";
import { Alert, AlertTitle, Box, Button, ButtonGroup, Paper, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography, styled, toggleButtonClasses, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { useSessionStorage } from "react-use";


const Subtitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
}));

const OptionBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(-7),
    alignItems: 'stretch',
    marginBottom: theme.spacing(2),

    [`& .${toggleButtonClasses.root}`]: {
        minWidth: theme.spacing(6),
        minHeight: theme.spacing(6),
        padding: 0
    }    
}));


const StyledTableHead = styled(TableHead)(({ theme }) => ({
    '& th': {
        padding: theme.spacing(2)
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },

    '& td': {
        padding: theme.spacing(2)
    },

    [`& .${timelineDotClasses.root}`]: {
        margin: theme.spacing(0),
        width: theme.spacing(5),
        height: theme.spacing(5),
        alignItems: 'center',
        justifyContent: 'center'
    },

    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const StyledTimelineOppositeContent = styled(TimelineOppositeContent)(({ theme }) => ({
    margin: '11px 0 0 0', 
    flex: 'unset',
    minWidth: theme.spacing(22),
    maxWidth: theme.spacing(22),
    paddingLeft: 0,
    paddingRight: theme.spacing(2), 
}));

const StyledTimelineContent = styled(TimelineContent)(({ theme }) => ({
    paddingLeft: theme.spacing(2), 
    //px: 2, 
    flex: 'unset',
    marginTop: 9,
    paddingRight: 0
}));

interface ISourceDataItemAuditTimelineItemProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    trial?: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant?: Participant;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;
    
    audit: SourceDataItemAudit;
    index: number;
    total: number;
    
}

const SourceDataItemAuditTimelineItem: FC<ISourceDataItemAuditTimelineItemProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,
    
    metadata,
    trialConfiguration,
    configuration,
    sourceData,
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,

    audit,
    index,
    total
}) => {

    // const oldItem = useItem(
    //     metadata,
    //     trialConfiguration,
    //     configuration ?? trialConfiguration,
    //     new SourceData({
    //         date: undefined,
    //         events: undefined,
    //         forms: undefined,
    //         items: {
    //             [studyEventOid!]: {
    //                 [studyEventRepeat!]: {
    //                     [caseReportFormOid!]: {
    //                         [itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
    //                             [itemGroupRepeat!]: {
    //                                 [itemName!]: new SourceDataItem({
    //                                     value: audit.oldValue
    //                                 })
    //                             }
    //                         }
    //                     }
    //                 }
    //             }
    //         }
    //     }),
    //     trial,
    //     masterGroup,
    //     coordinatingGroup,
    //     institution,
    //     participant,
    //     studyEventOid,
    //     studyEventRepeat,
    //     caseReportFormOid,
    //     caseReportFormVersionOid,
    //     itemGroupName,
    //     itemGroupRepeat,
    //     itemName
    // )

    const newItem = useItem(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        new SourceData({
            date: undefined,
            events: undefined,
            forms: undefined,
            items: {
                [studyEventOid!]: {
                    [studyEventRepeat!]: {
                        [caseReportFormOid!]: {
                            [itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
                                [itemGroupRepeat!]: {
                                    [itemName!]: new SourceDataItem({
                                        value: audit.newValue
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }),
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    )

    // const OldValueComponent = SourceDataItemMapping[oldItem?.itemMetadata.fieldType ?? SourceDataFieldType.Text];
    const NewValueComponent = SourceDataItemMapping[newItem?.itemMetadata.fieldType ?? SourceDataFieldType.Text];

    return (
        <TimelineItem>
            <StyledTimelineOppositeContent
                align="right"
                variant="body2"
                color="text.secondary"
            >
                {audit.auditDate && DateTime.fromISO(audit.auditDate).toFormat('dd/MM/yyyy hh:mm:ss a')}
            </StyledTimelineOppositeContent>
            <TimelineSeparator>
                <TimelineDot
                    color={
                        audit.auditType === AuditType.Inserted ?
                            'success' :
                            audit.auditType === AuditType.Updated ?
                                'info' :
                                audit.auditType === AuditType.Deleted ?
                                    'error' :
                                    'grey'
                    }
                >
                    <FontAwesomeIcon 
                        icon={
                            audit.auditType === AuditType.Inserted ?
                                AppSettings.icons.auditInsert :
                                audit.auditType === AuditType.Updated ?
                                    AppSettings.icons.auditUpdate :
                                    audit.auditType === AuditType.Deleted ?
                                        AppSettings.icons.auditDelete :
                                        AppSettings.icons.auditUnknown
                        }
                    />
                </TimelineDot>
                <TimelineConnector 
                    sx={{
                        visibility: index + 1 === total ?
                            'hidden' :
                            undefined
                    }}
                />
            </TimelineSeparator>
            <StyledTimelineContent>
                <Typography variant="h6" component="span" sx={{lineHeight: 1.4}}>
                    {auditTypeToText(audit.auditType)} by {audit.editorFirstName} {audit.editorLastName}
                </Typography>
                <NewValueComponent itemData={newItem} hideBorderStyles={false} hideStyles={false} />
            </StyledTimelineContent>
        </TimelineItem>
    )
}

interface ISourceDataItemAuditRowProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    trial?: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant?: Participant;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;
    
    audit: SourceDataItemAudit;
    
}

const SourceDataItemAuditRow: FC<ISourceDataItemAuditRowProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,
    
    metadata,
    trialConfiguration,
    configuration,
    sourceData,
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,

    audit
}) => {

    // const oldItem = useItem(
    //     metadata,
    //     trialConfiguration,
    //     configuration ?? trialConfiguration,
    //     new SourceData({
    //         date: undefined,
    //         events: undefined,
    //         forms: undefined,
    //         items: {
    //             [studyEventOid!]: {
    //                 [studyEventRepeat!]: {
    //                     [caseReportFormOid!]: {
    //                         [itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
    //                             [itemGroupRepeat!]: {
    //                                 [itemName!]: new SourceDataItem({
    //                                     value: audit.oldValue
    //                                 })
    //                             }
    //                         }
    //                     }
    //                 }
    //             }
    //         }
    //     }),
    //     trial,
    //     masterGroup,
    //     coordinatingGroup,
    //     institution,
    //     participant,
    //     studyEventOid,
    //     studyEventRepeat,
    //     caseReportFormOid,
    //     caseReportFormVersionOid,
    //     itemGroupName,
    //     itemGroupRepeat,
    //     itemName
    // )

    const newItem = useItem(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        new SourceData({
            date: undefined,
            events: undefined,
            forms: undefined,
            items: {
                [studyEventOid!]: {
                    [studyEventRepeat!]: {
                        [caseReportFormOid!]: {
                            [itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
                                [itemGroupRepeat!]: {
                                    [itemName!]: new SourceDataItem({
                                        value: audit.newValue
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }),
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    )

    // const OldValueComponent = SourceDataItemMapping[oldItem?.itemMetadata.fieldType ?? SourceDataFieldType.Text];
    const NewValueComponent = SourceDataItemMapping[newItem?.itemMetadata.fieldType ?? SourceDataFieldType.Text];

    return (
        <StyledTableRow>
            <TableCell>
                {audit.auditDate && DateTime.fromISO(audit.auditDate).toFormat('dd/MM/yyyy hh:mm:ss a')}
            </TableCell>
            <TableCell>
                <TimelineDot
                    color={
                        audit.auditType === AuditType.Inserted ?
                            'success' :
                            audit.auditType === AuditType.Updated ?
                                'info' :
                                audit.auditType === AuditType.Deleted ?
                                    'error' :
                                    'grey'
                    }
                >
                    <FontAwesomeIcon 
                        fixedWidth
                        icon={
                            audit.auditType === AuditType.Inserted ?
                                AppSettings.icons.auditInsert :
                                audit.auditType === AuditType.Updated ?
                                    AppSettings.icons.auditUpdate :
                                    audit.auditType === AuditType.Deleted ?
                                        AppSettings.icons.auditDelete :
                                        AppSettings.icons.auditUnknown
                        }
                    />
                </TimelineDot>
            </TableCell>
            {/* <TableCell>
                <OldValueComponent itemData={oldItem} hideBorderStyles={true} hideStyles={false} />
            </TableCell> */}
            <TableCell>
                <NewValueComponent itemData={newItem} hideBorderStyles={true} hideStyles={false} />
            </TableCell>
            <TableCell>
                {audit.editorFirstName} {audit.editorLastName}
            </TableCell>
        </StyledTableRow>
    )
}

interface ISourceDataItemAuditListProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    
    audits?: SourceDataItemAudit[];
}

const SourceDataItemAuditList: FC<ISourceDataItemAuditListProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,
    audits
}) => {
    const theme = useTheme();
    const { idMap: trials } = useContext(TrialsContext);
    const { idMap: masterGroups } = useContext(MasterGroupsContext);
    const { idMap: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { idMap: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const trial = trialId ? trials?.[trialId] : undefined;
    const masterGroup = masterGroupId ? masterGroups?.[masterGroupId] : undefined;
    const coordinatingGroup = coordinatingGroupId ? coordinatingGroups?.[coordinatingGroupId] : undefined;
    const institution = institutionId ? institutions?.[institutionId] : undefined;
    const participant = participantId ? participants?.[participantId] : undefined;
    
    const [displayType, setDisplayType] = useSessionStorage<'table' | 'timeline' | undefined>(`audit-display`, 'table')

    return (
        <>
            <Subtitle variant="h2">
                Data Audit
            </Subtitle>
            <OptionBox>
                <Paper>
                    <ToggleButtonGroup
                        exclusive
                        value={displayType}
                    >
                        <Tooltip
                            title="Table"
                        >
                            <ToggleButton 
                                sx={{ border: 'none' }}
                                value="table" 
                                selected={displayType === 'table'}
                                onChange={(e, newValue) => setDisplayType(newValue ?? 'table')}
                            >
                                <FontAwesomeIcon 
                                    size="lg"
                                    fixedWidth
                                    icon={
                                        AppSettings.icons.auditTable
                                    }
                                />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip
                            title="Timeline"
                        >
                            <ToggleButton 
                                sx={{ border: 'none' }}
                                value="timeline" 
                                selected={displayType === 'timeline'}
                                onChange={(e, newValue) => setDisplayType(newValue ?? 'timeline')}
                            >
                                <FontAwesomeIcon 
                                    fixedWidth
                                    size="lg"
                                    icon={
                                        AppSettings.icons.auditTimeline
                                    }
                                />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </Paper>
            </OptionBox>
            {
                (!audits || audits.length === 0) && (
                    <Alert
                        severity="warning"
                    >
                        <AlertTitle>No Data Audit</AlertTitle>
                        There is currently no audit information for this data point.
                    </Alert> 
                )
            }
            {
                audits && audits.length > 0 && (
                    <Paper>
                        {
                            displayType === 'table' && (
                                <Table
                                    sx={{
                                        tableLayout: 'fixed'
                                    }}
                                >
                                    <StyledTableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    width: theme.spacing(24.375)
                                                }}
                                            >
                                                Date
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    width: theme.spacing(7.25)
                                                }}
                                            >
        
                                            </TableCell>
                                            {/* <TableCell
                                                sx={{
                                                    width: '50%'
                                                }}
                                            >
                                                Old Value
                                            </TableCell> */}
                                            <TableCell
                                                sx={{
                                                    width: '100%'
                                                }}
                                            >
                                                {/* New */}Value 
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    width: theme.spacing(24.375)
                                                }}
                                            >
                                                User
                                            </TableCell>
                                        </TableRow>
                                    </StyledTableHead>
                                    <TableBody>
                                        {
                                            audits.map((audit, i) => (
                                                <SourceDataItemAuditRow
                                                    key={i}
                                                    
                                                    trialId={trialId}
                                                    masterGroupId={masterGroupId}
                                                    coordinatingGroupId={coordinatingGroupId}
                                                    institutionId={institutionId}
                                                    participantId={participantId}
                                                    studyEventOid={studyEventOid}
                                                    studyEventRepeat={studyEventRepeat}
                                                    caseReportFormOid={caseReportFormOid}
                                                    caseReportFormVersionOid={caseReportFormVersionOid}
                                                    itemGroupName={itemGroupName}
                                                    itemGroupRepeat={itemGroupRepeat}
                                                    itemName={itemName}
                                    
                                                    trial={trial}
                                                    masterGroup={masterGroup}
                                                    coordinatingGroup={coordinatingGroup}
                                                    institution={institution}
                                                    participant={participant}
                                                    metadata={metadata}
                                                    trialConfiguration={trialConfiguration}
                                                    configuration={configuration}
                                                    sourceData={sourceData}
        
                                                    audit={audit}
                                                />
                                            ))
                                        }
                                    </TableBody>
                                </Table>
                            )
                        }
                        
                        {
                            displayType === 'timeline' && (
                                <Timeline> 
                                    {
                                        audits.map((audit, i) => (
                                            <SourceDataItemAuditTimelineItem
                                                key={i}
                                                
                                                trialId={trialId}
                                                masterGroupId={masterGroupId}
                                                coordinatingGroupId={coordinatingGroupId}
                                                institutionId={institutionId}
                                                participantId={participantId}
                                                studyEventOid={studyEventOid}
                                                studyEventRepeat={studyEventRepeat}
                                                caseReportFormOid={caseReportFormOid}
                                                caseReportFormVersionOid={caseReportFormVersionOid}
                                                itemGroupName={itemGroupName}
                                                itemGroupRepeat={itemGroupRepeat}
                                                itemName={itemName}
                                
                                                trial={trial}
                                                masterGroup={masterGroup}
                                                coordinatingGroup={coordinatingGroup}
                                                institution={institution}
                                                participant={participant}
                                                metadata={metadata}
                                                trialConfiguration={trialConfiguration}
                                                configuration={configuration}
                                                sourceData={sourceData}

                                                audit={audit}
                                                index={i}
                                                total={audits.length}
                                            />
                                        ))
                                    }
                                </Timeline>
                            )
                        }
                    </Paper>
                )
            }
        </>
    )
}

export default SourceDataItemAuditList;